var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import useUpdateEffect from '../../utils/useUpdateEffect';
import { dragStyles, indicatorTrackStyles, sliderColors, steppingStyles, } from './constants';
import { SliderHandle } from './SliderHandle';
import { SliderMarker } from './SliderMarker';
import { SliderTrack } from './SliderTrack';
const transitionDuration = (parseInt(theme.durationDefault, 10));
const getParentWindowForRef = (ref) => {
    var _a;
    return ((_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.ownerDocument.defaultView);
};
const sliderContainerStyles = css `
  ${styleUtils.borderBox}
  padding-left: ${styleUtils.rem(10)};
  padding-right: ${styleUtils.rem(10)};
`;
const sliderStyles = css `
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  display: flex;
  height: ${styleUtils.rem(20)};
  outline: 0;
  position: relative;
  touch-action: none;
  user-select: none;
  width: 100%;
`;
const defaultProps = {
    className: '',
    color: sliderColors.green,
    dragStyle: dragStyles.elastic,
    hasMarkers: false,
    hasTooltip: true,
    id: '',
    indicatorTrackStyle: indicatorTrackStyles.segmented,
    isUserInteractable: true,
    name: '',
    onChange: () => { },
    onPointerUp: () => { },
    steppingStyle: steppingStyles.segments,
    steppingValue: 1,
};
const Slider = ({ ariaDescribedBy, ariaLabel, ariaLabelledBy, className = defaultProps.className, color = defaultProps.color, dragStyle = defaultProps.dragStyle, hasMarkers = defaultProps.hasMarkers, hasTooltip = defaultProps.hasTooltip, id = defaultProps.id, indicatorTrackStyle = defaultProps.indicatorTrackStyle, isUserInteractable = defaultProps.isUserInteractable, markers, maximumValue, minimumValue, name = defaultProps.name, onChange = defaultProps.onChange, onPointerUp = defaultProps.onPointerUp, steppingStyle = defaultProps.steppingStyle, steppingValue = defaultProps.steppingValue, value, }) => {
    // --- Refs
    const isDraggingRef = useRef(false);
    const onChangeRef = useRef();
    const sliderHandleRef = useRef(null);
    const sliderRef = useRef(null);
    // --- Animations
    const [hasIndicatorCssAnimation, setIndicatorCssAnimation,] = useState(true);
    const [isDragging, setIsDragging,] = useState(false);
    const [isHovering, setIsHovering,] = useState(false);
    const [isTransitioning, setIsTransitioning,] = useState(false);
    const [transitionSliderHandleX, setTransitionSliderHandleX,] = useState(null);
    // --- Values
    const [clampedComputedValue, setClampedComputedValue,] = useState(value || 0);
    const [computedValue, setComputedValue,] = useState(value || 0);
    const [indicatorRelativePosition, setIndicatorRelativePosition,] = useState(0);
    const [label, setLabel,] = useState(String(value || 0));
    const [outputValue, setOutputValue,] = useState(value || 0);
    const [snappedIndicatorRelativePosition, setSnappedIndicatorRelativePosition,] = useState(null);
    const [snappedValue, setSnappedValue,] = useState(value || 0);
    // ---
    // `isDraggingRef`
    // ---
    isDraggingRef.current = isDragging;
    // ---
    // `onChangeRef`
    // ---
    // This exists to prevent double-firing and the potential for infinite loops when `onChange` changes and `useEffect` gets executed again.
    onChangeRef.current = onChange;
    // ---
    // `numberOfDecimalPlaces`
    // ---
    const numberOfDecimalPlaces = (useMemo(() => {
        const decimals = (steppingValue
            && (steppingValue
                .toString()
                .split('.')[1]));
        return (decimals
            ? (decimals
                .length)
            : 0);
    }, [
        steppingValue,
    ]));
    // ---
    // `range`
    // ---
    const range = (useMemo(() => (maximumValue - minimumValue), [
        maximumValue,
        minimumValue,
    ]));
    const hasSteppedValueOutOfRange = (useCallback(() => {
        const highestSteppedValue = (minimumValue + Math.ceil(range / steppingValue) * steppingValue);
        return highestSteppedValue > maximumValue;
    }, [
        maximumValue,
        minimumValue,
        range,
        steppingValue,
    ]));
    // ---
    // `computedMarkers`
    // ---
    const computedMarkers = (useMemo(() => {
        const getRelativePositions = (unformattedMarkers) => (unformattedMarkers
            .map((_a) => {
            var { position } = _a, props = __rest(_a, ["position"]);
            return (Object.assign(Object.assign({}, props), { position, relativePosition: (position - minimumValue) / range }));
        }));
        if (!hasMarkers) {
            return [];
        }
        if (markers) {
            return (getRelativePositions(markers));
        }
        const lastMarkerCount = (hasSteppedValueOutOfRange()
            ? 0
            : 1);
        const numberOfMarkers = (Math.ceil(range / steppingValue) + lastMarkerCount);
        return (getRelativePositions(Array.from(Array(numberOfMarkers))
            .map((item, index) => (minimumValue + (index * steppingValue)))
            .map((markerValue) => ({
            position: markerValue,
        }))));
    }, [
        hasMarkers,
        hasSteppedValueOutOfRange,
        markers,
        minimumValue,
        range,
        steppingValue,
    ]));
    // ---
    // `computedValue`
    // ---
    useEffect(() => {
        if (isDraggingRef.current) {
            return;
        }
        setComputedValue(value);
    }, [
        setComputedValue,
        value,
    ]);
    // ---
    // `clampedComputedValue`
    // ---
    useEffect(() => {
        setClampedComputedValue(Math.min(Math.max(minimumValue, computedValue), maximumValue));
    }, [
        computedValue,
        minimumValue,
        maximumValue,
        setClampedComputedValue,
    ]);
    // ---
    // `snappedValue`
    // ---
    useEffect(() => {
        const getClampedValue = (localSnappedValue) => {
            if (localSnappedValue > maximumValue) {
                return localSnappedValue - steppingValue;
            }
            return localSnappedValue;
        };
        if (steppingStyle === steppingStyles.segments
            && steppingValue) {
            const binaryMathSnappedValue = (Math.round((clampedComputedValue - minimumValue) / steppingValue)
                * steppingValue + minimumValue);
            const decimalMultiplier = 10 ** numberOfDecimalPlaces;
            const decimalMathSnappedValue = (Math.round(binaryMathSnappedValue * decimalMultiplier) / decimalMultiplier);
            setSnappedValue(getClampedValue(decimalMathSnappedValue));
        }
        else if (steppingStyle === steppingStyles.markers
            && computedMarkers.length > 0) {
            setSnappedValue(computedMarkers
                .map(({ position, }) => ({
                difference: Math.abs(position - clampedComputedValue),
                position,
            }))
                .reduce((closestMarker, marker) => (marker.difference < closestMarker.difference
                ? marker
                : closestMarker))
                .position);
        }
        else {
            setSnappedValue(clampedComputedValue);
        }
    }, [
        clampedComputedValue,
        computedMarkers,
        indicatorTrackStyle,
        maximumValue,
        minimumValue,
        numberOfDecimalPlaces,
        setSnappedValue,
        steppingStyle,
        steppingValue,
    ]);
    // ---
    // `indicatorRelativePosition`
    // `snappedIndicatorRelativePosition`
    // ---
    const calculateValueInRange = (useCallback((unscaledValue) => ((unscaledValue - minimumValue) / range), [
        minimumValue,
        range,
    ]));
    const getQuarterSteppingValue = (useCallback((steppingDirection) => {
        if (steppingStyle === steppingStyles.segments
            && steppingValue) {
            return steppingValue / 4;
        }
        if (steppingStyle === steppingStyles.markers
            && computedMarkers.length > 0) {
            const selectedMarkerIndex = (computedMarkers
                .findIndex(({ position, }) => (position === snappedValue)));
            const selectedMarkerPosition = computedMarkers[selectedMarkerIndex].position;
            const edgePosition = (steppingDirection > 0
                ? maximumValue
                : minimumValue);
            const nextMarkerPosition = (computedMarkers[selectedMarkerIndex + steppingDirection]
                ? computedMarkers[selectedMarkerIndex + steppingDirection].position
                : edgePosition);
            return Math.abs(nextMarkerPosition - selectedMarkerPosition) / 4;
        }
        return 0;
    }, [
        computedMarkers,
        maximumValue,
        minimumValue,
        snappedValue,
        steppingStyle,
        steppingValue,
    ]));
    const calculateElasticValue = (useCallback((smoothValue) => {
        const strengthDivider = 0.6; // Lower is stronger, but it can be no lower than 0.5.
        const steppingRange = smoothValue - snappedValue;
        const steppingDirection = (steppingRange > 0
            ? 1
            : -1);
        const quarterSteppingValue = (getQuarterSteppingValue(steppingDirection));
        const elasticIndicatorValue = (smoothValue
            - (steppingRange / (strengthDivider * 2))
            + (Math.abs(steppingRange) > quarterSteppingValue
                ? (steppingRange - (quarterSteppingValue * steppingDirection)) / strengthDivider
                : 0));
        const clampedElasticIndicatorValue = (Math.min(Math.max(minimumValue, elasticIndicatorValue), maximumValue));
        return clampedElasticIndicatorValue;
    }, [
        getQuarterSteppingValue,
        maximumValue,
        minimumValue,
        snappedValue,
    ]));
    useEffect(() => {
        if (isDragging) {
            if (dragStyle === dragStyles.smooth) {
                setSnappedIndicatorRelativePosition(null);
                setIndicatorRelativePosition(calculateValueInRange(clampedComputedValue));
            }
            else if (dragStyle === dragStyles.elastic) {
                setSnappedIndicatorRelativePosition(null);
                setIndicatorRelativePosition(calculateValueInRange(calculateElasticValue(clampedComputedValue)));
            }
        }
        else if (indicatorTrackStyle === indicatorTrackStyles.continuous) {
            setIndicatorRelativePosition(calculateValueInRange(clampedComputedValue));
        }
    }, [
        clampedComputedValue,
        calculateElasticValue,
        calculateValueInRange,
        dragStyle,
        indicatorTrackStyle,
        isDragging,
        setSnappedIndicatorRelativePosition,
        setIndicatorRelativePosition,
    ]);
    useEffect(() => {
        if (isDragging) {
            return;
        }
        if (indicatorTrackStyle === indicatorTrackStyles.segmented) {
            setSnappedIndicatorRelativePosition(calculateValueInRange(snappedValue));
        }
    }, [
        calculateValueInRange,
        indicatorTrackStyle,
        isDragging,
        setSnappedIndicatorRelativePosition,
        snappedValue,
    ]);
    // ---
    // `snappedIndicatorRelativePosition`
    // ---
    const onSliderHandleTransitionEnded = (useCallback(({ propertyName, }) => {
        if (propertyName !== 'left') {
            return;
        }
        setIsTransitioning(false);
    }, [
        setIsTransitioning,
    ]));
    useEffect(() => {
        const sliderHandleDomElement = (sliderHandleRef
            .current);
        sliderHandleDomElement === null || sliderHandleDomElement === void 0 ? void 0 : sliderHandleDomElement.addEventListener('transitionend', onSliderHandleTransitionEnded);
        return () => {
            sliderHandleDomElement === null || sliderHandleDomElement === void 0 ? void 0 : sliderHandleDomElement.removeEventListener('transitionend', onSliderHandleTransitionEnded);
        };
    }, [
        onSliderHandleTransitionEnded,
    ]);
    useEffect(() => {
        var _a;
        if (isTransitioning
            || snappedIndicatorRelativePosition === null) {
            return;
        }
        setTransitionSliderHandleX((_a = sliderHandleRef === null || sliderHandleRef === void 0 ? void 0 : sliderHandleRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().x);
        setIndicatorRelativePosition(snappedIndicatorRelativePosition);
        setSnappedIndicatorRelativePosition(null);
    }, [
        isTransitioning,
        setIndicatorRelativePosition,
        setSnappedIndicatorRelativePosition,
        setTransitionSliderHandleX,
        snappedIndicatorRelativePosition,
    ]);
    // ---
    // `label`
    // `outputValue`
    // ---
    useEffect(() => {
        const getFormattedNumberValue = (selectedValue) => (typeof selectedValue === 'number'
            ? selectedValue.toFixed(numberOfDecimalPlaces)
            : selectedValue);
        if (markers) {
            const getMarkerLabel = (selectedValue) => (typeof selectedValue === 'object'
                ? selectedValue.label || snappedValue
                : selectedValue);
            const getMarkerValue = (selectedValue) => (typeof selectedValue === 'object'
                ? selectedValue.value || snappedValue
                : selectedValue);
            const selectedValue = (markers
                .find(({ position, }) => (position === snappedValue))
                || snappedValue);
            setOutputValue(getMarkerValue(selectedValue));
            setLabel(getFormattedNumberValue(getMarkerValue(getMarkerLabel(selectedValue))));
        }
        else {
            setOutputValue(snappedValue);
            setLabel(getFormattedNumberValue(snappedValue));
        }
    }, [
        markers,
        setLabel,
        numberOfDecimalPlaces,
        setOutputValue,
        snappedValue,
    ]);
    // ---
    // `onChange`
    // ---
    useUpdateEffect(() => {
        if (!isDraggingRef.current) {
            return;
        }
        if (onChangeRef.current !== undefined) {
            onChangeRef.current(outputValue);
        }
    }, [
        onChangeRef,
        outputValue,
    ]);
    // ---
    // `isHovering`
    // ---
    const onPointerEnter = (useCallback(() => {
        setIsHovering(true);
    }, [
        setIsHovering,
    ]));
    const onPointerLeave = (useCallback(() => {
        setIsHovering(false);
    }, [
        setIsHovering,
    ]));
    // ---
    // `computedValue`
    // ---
    const onSliderMoved = (useCallback((event) => {
        var _a;
        const { clientX: pointerX, } = event;
        (_a = getParentWindowForRef(sliderRef)) === null || _a === void 0 ? void 0 : _a.requestAnimationFrame(() => {
            if (sliderRef.current) {
                const { left: sliderLeft, width: sliderWidth, } = sliderRef.current.getBoundingClientRect();
                setComputedValue(((pointerX - sliderLeft) / sliderWidth)
                    * range
                    + minimumValue);
            }
        });
    }, [
        minimumValue,
        range,
        setComputedValue,
        sliderRef,
    ]));
    useEffect(() => {
        if (!isDragging) {
            return () => { };
        }
        const parentWindow = getParentWindowForRef(sliderRef);
        parentWindow === null || parentWindow === void 0 ? void 0 : parentWindow.addEventListener('pointermove', onSliderMoved);
        return () => {
            parentWindow === null || parentWindow === void 0 ? void 0 : parentWindow.removeEventListener('pointermove', onSliderMoved);
        };
    }, [
        isDragging,
        onSliderMoved,
    ]);
    // ---
    // `isDragging`
    // `isTransitioning`
    // ---
    const startDragging = (useCallback((event) => {
        var _a;
        setIsDragging(true);
        // `transitionstart` isn't working in Safari v13.0.5, so this needs to happen on `mousedown` instead.
        if (transitionSliderHandleX
            !== ((_a = sliderHandleRef === null || sliderHandleRef === void 0 ? void 0 : sliderHandleRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().x)) {
            setIsTransitioning(true);
        }
        onSliderMoved(event);
    }, [
        onSliderMoved,
        setIsDragging,
        setIsTransitioning,
        sliderHandleRef,
        transitionSliderHandleX,
    ]));
    const stopDragging = (useCallback(() => {
        setIndicatorCssAnimation(true);
        setIsDragging(false);
        onPointerUp(outputValue);
    }, [
        onPointerUp,
        outputValue,
        setIndicatorCssAnimation,
        setIsDragging,
    ]));
    useEffect(() => {
        if (!isDragging) {
            return () => { };
        }
        const parentWindow = getParentWindowForRef(sliderRef);
        parentWindow === null || parentWindow === void 0 ? void 0 : parentWindow.addEventListener('pointerup', stopDragging);
        return () => {
            parentWindow === null || parentWindow === void 0 ? void 0 : parentWindow.removeEventListener('pointerup', stopDragging);
        };
    }, [
        isDragging,
        stopDragging,
    ]);
    // ---
    // `hasIndicatorCssAnimation`
    // ---
    useEffect(() => {
        if (!hasIndicatorCssAnimation) {
            return () => { };
        }
        const lastChangeTime = performance.now();
        return () => {
            if (performance.now() - lastChangeTime < transitionDuration) {
                setIndicatorCssAnimation(false);
                // `transitionend` doesn't fire if we remove the transition so we need to stop it here.
                setIsTransitioning(false);
            }
        };
    }, [
        clampedComputedValue,
        hasIndicatorCssAnimation,
        setIndicatorCssAnimation,
        setIsTransitioning,
    ]);
    const onContextMenu = (useCallback((event) => {
        event.preventDefault();
    }, []));
    // ---
    // Render
    // ---
    return (_jsx("div", Object.assign({ className: className, css: sliderContainerStyles }, { children: _jsxs("div", Object.assign({ "aria-describedby": ariaDescribedBy, "aria-label": ariaLabel || name, "aria-labelledby": ariaLabelledBy, css: css `
            ${sliderStyles}
            ${isUserInteractable && css `cursor: pointer;`}
          `, onContextMenu: onContextMenu, onPointerDown: isUserInteractable
                ? startDragging
                : () => { }, onPointerEnter: onPointerEnter, onPointerLeave: onPointerLeave, ref: sliderRef, role: "button", tabIndex: 0, "touch-action": "none" }, { children: [_jsx(SliderTrack, { color: color, hasIndicatorCssAnimation: hasIndicatorCssAnimation, relativeWidth: indicatorRelativePosition }), computedMarkers
                    .map(({ content, position, relativePosition, }) => (_jsx(SliderMarker, { color: color, content: content, hasIndicatorCssAnimation: hasIndicatorCssAnimation, isSelected: indicatorRelativePosition >= relativePosition, relativePosition: relativePosition }, position))), _jsx(SliderHandle, { ariaDescribedBy: ariaDescribedBy, ariaLabel: ariaLabel || name, ariaLabelledBy: ariaLabelledBy, color: color, hasIndicatorCssAnimation: hasIndicatorCssAnimation, hasTooltip: hasTooltip, isDragging: isDragging, isHovering: isHovering, isUserInteractable: isUserInteractable, ref: sliderHandleRef, relativePosition: indicatorRelativePosition, tooltipContent: label }), _jsx("input", { hidden: true, id: id, name: name, readOnly: true, value: outputValue })] })) })));
};
const MemoizedSlider = memo(Slider);
export { MemoizedSlider as Slider };
// MC-1068 Temporary hack for Storybook.
export default MemoizedSlider;
